import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Space,
  Tooltip,
  Popconfirm,
  notification,
  Select,
  Typography,
  Divider, Button
} from "antd";
import { audioMessage as message } from '../Common/Notifications/audioMessage';
import { TrainingAPI } from "../../api/TrainingAPI";
import { PermissionedInput, PermissionedSelect, PermissionedButton, PermissionedPopConfirm } from '../PermissionedComponents';
import { useSelector } from "react-redux";
import { AdminNotice } from "../Settings/AdminNotice";
import {useAuth0} from "@auth0/auth0-react";

const { Option } = Select;


export const NicknameTable = ({ missingPlayerName, players, modalMode=false}) => {
  const [nickname, setNickname] = useState(undefined);
  const [selectedPlayerId, setSelectedPlayerId] = useState(undefined);
  const [data, setData] = useState([]);
  const [unRecognizedNameData, setUnRecognizedNameData] = useState([]);
  const trainingAPI = new TrainingAPI();
  const playerDropdownRef = useRef();
  const [playerDropdownOpen, setOpen] = useState(false);
  const { user } = useAuth0();


  const { info : {unrecognized_names_enabled} } = useSelector(({team}) => team);
  const isStaffEmail = user?.email?.endsWith("gpsdataviz.com");

  useEffect(() => {
    if (missingPlayerName) {
      setNickname(missingPlayerName)
    }
  }, [missingPlayerName]);

  const handleDelete = (alternate_id) => {
    trainingAPI
      .deleteAlternateName(alternate_id)
      .then((data) => {
        message.success("Nickname deleted");
        getNicknames();
      })
      .catch((data) => message.error("Error deleting nickname"));
  };

  const onNicknameChange = (e) => {
    setNickname(e.target.value);
  };

  const onPlayerChange = (val) => {
    setSelectedPlayerId(val);
  };

  useEffect(() => {
    getNicknames();
    getUnrecognizedNames();
  }, []);

  const getNicknames = () => {
    trainingAPI.getAlternateNames().then((data) => {
      setData(data);
    });
  };

  const getUnrecognizedNames = () => {
    trainingAPI.getUnrecognizedNames().then((data) => {setUnRecognizedNameData(data)});
  }

  const onSubmit = () => {
    trainingAPI
      .addAlternateName(nickname, selectedPlayerId)
      .then((data) => {
        message.success("Nickname added");
        getNicknames();
        getUnrecognizedNames();
        setNickname(undefined);
        setSelectedPlayerId(undefined);
        notification.destroy('nickname-notification');
      })
      .catch((data) => message.error("Error adding nickname"));
  };

  const columns = [
    {
      title: "Nickname",
      dataIndex: "nickname",
      key: "nickname",
      sorter: {
        compare: (a, b) => a.nickname.localeCompare(b.nickname),
        multiple: 1,
      },
      render: (nickname, data) => {
        return <>{nickname}</>;
      },
    },
    {
      title: "Player Name",
      dataIndex: "player_id",
      key: "player_id",
      sorter: {
        compare: (a, b) => a.last_name.localeCompare(b.last_name),
        multiple: 3,
      },
      render: (player_id, data) => {
        return (
          <>
            {data.first_name} {data.last_name}
          </>
        );
      },
    },
    {
      title: "",
      dataIndex: "operation",
      width: "10%",
      render: (_, key) => (
        <PermissionedPopConfirm
          permissionCheck="DeleteNicknames"
          title="Sure to delete?"
          onConfirm={() => {
            handleDelete(key["alternate_name_id"]);
          }}
        >
          <a style={{ color: "#1890ff" }}>Delete</a>
        </PermissionedPopConfirm>
      ),
    },
  ];

  const addNicknameFromUnrecognizedNames = (name) => {
    setNickname(name);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setOpen(true);

  }

  const unRecognizedNamesColumns = [
    {
      title: "Unrecognized Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      sorter: {
        compare: (a, b) => a.name.localeCompare(b.name),
        multiple: 1,
      },
      render: (name) => {
        return <>{name}</>;
      }
    },
    {
      title: "First seen",
      dataIndex: "created_at",
      key: "created_at",
      width: "10%",
      sorter: {
        compare: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        multiple: 1,
      },
      render: (created_at) => {
        return <>{new Date(created_at).toLocaleDateString()}</>;
      }
    },
    {
      title: "Source",
      dataIndex: "sources",
      key: "sources",
      render: (names) => {
        return <>{(names || []).join(", ")}</>;
      }
    },
    {
      title: "Actions",
      dataIndex: undefined,
      key: undefined,
      width: "15%",
      render: (_, record) => {
        return <Typography.Link onClick={()=> addNicknameFromUnrecognizedNames(record.name)}>Add Nickname</Typography.Link>;
      }
    }
  ]

  return (
    <>
      <div className="report-injury-header2">Add Nicknames</div>
      <br />
      <Space>
        <div className="injury-item">
          <PermissionedInput
            permissionCheck="AddNicknames"
            style={{ width: 200 }}
            placeholder="Nickname"
            value={nickname}
            onChange={onNicknameChange}
          />
        </div>
        <div className="injury-item" ref={playerDropdownRef}>
          <PermissionedSelect
            permissionCheck="AddNicknames"
            showSearch
            style={{ width: 200 }}
            placeholder="Player Name"
            value={selectedPlayerId}
            onChange={onPlayerChange}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            open={playerDropdownOpen}
            onDropdownVisibleChange={(visible) => setOpen(visible)}
          >
            {players !== null ? (
              players.map((player, i) => {
                return (
                  <Option
                    key={i}
                    value={player.player_id}
                  >
                    {`${player.first_name} ${player.last_name} #${player.number}`}
                  </Option>
                );
              })
            ) : (
              <></>
            )}
          </PermissionedSelect>
        </div>
        {nickname === undefined || selectedPlayerId === undefined ? (
          <Tooltip title="Not all fields selected">
            <PermissionedButton 
              permissionCheck="AddNicknames"
              type="primary" 
              disabled
            >
              Add Nickname
            </PermissionedButton>
          </Tooltip>
        ) : (
          <PermissionedButton 
            permissionCheck="AddNicknames"
            type="primary" 
            onClick={onSubmit}
          >
            Add Nickname
          </PermissionedButton>
        )}
      </Space>
      <div className="report-injury-header2">Current Nicknames</div>
      <Space>
        <Table
          rowKey="rowId"
          columns={columns}
          size="small"
          dataSource={data}
          style={{ width: 440, maxWidth: "100%" }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30"],
            position: ["topLeft"],
          }}
        />
      </Space>

      {
        ((unrecognized_names_enabled || isStaffEmail) && !modalMode) ? (
        <>
         <Divider  style={{ margin: "20px 0" }} />
        <div>
          <div className="report-injury-header2">Unrecognized Names</div>

          {(!unrecognized_names_enabled && isStaffEmail) && (
            <div style={{ margin: '16px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
              <AdminNotice />
            </div>
          )}

          <Typography.Text type="secondary">
            This is a list of names that have been found in the third party data that were not found on your GPS DataViz Roster. <br/>
            If these are alternate names for existing players, you can create a nickname to start tracking them in the data. <br/>
            If these are new players, you can add them to your roster in the Roster page.
          </Typography.Text>

          <div style={{marginTop: "20px"}}>
            <Table
              columns={unRecognizedNamesColumns}
              size="small"
              dataSource={unRecognizedNameData}
            />
          </div>
        </div>
        </>) : null
      }
    </>
  );
};
